import React, { useState, useEffect } from 'react';
import { Button, Card, Form, Modal, Row, Col, ListGroup, Container } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './Training.css';

function Training() {
    const [challenges, setChallenges] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedChallenge, setSelectedChallenge] = useState(null);
    const [newChallenge, setNewChallenge] = useState({
        title: '',
        category: 'OSINT',
        description: '',
        file_link: '',
        flag: '',
        difficulty: 'Medium'
    });
    const [submissionStatus, setSubmissionStatus] = useState(null);
    const [flagInput, setFlagInput] = useState('');
    const [createError, setCreateError] = useState(null);

    const [filters, setFilters] = useState({
        search: '',
        category: 'All',
        difficulty: 'All'
    });

    useEffect(() => {
        fetchChallenges();
    }, []);

    const fetchChallenges = () => {
        fetch('https://usscfedora.org/training.php', {
            method: 'GET',
            credentials: 'include',
        })
        .then(response => response.json())
        .then(data => {
            if (data.status === 'success') {
                setChallenges(data.challenges);
            }
        })
        .catch(error => console.error('Error fetching challenges:', error));
    };

    const getFilteredChallenges = () => {
        return challenges.filter(challenge => {
            const matchesSearch = challenge.title.toLowerCase().includes(filters.search.toLowerCase()) ||
                                challenge.description.toLowerCase().includes(filters.search.toLowerCase());
            const matchesCategory = filters.category === 'All' || challenge.category === filters.category;
            const matchesDifficulty = filters.difficulty === 'All' || challenge.difficulty === filters.difficulty;
            
            return matchesSearch && matchesCategory && matchesDifficulty;
        });
    };

    const handleCreateChallenge = () => {
        if (!newChallenge.title || !newChallenge.category || !newChallenge.description || !newChallenge.flag || !newChallenge.difficulty) {
            setCreateError('Please fill in all required fields');
            return;
        }
        
        setCreateError(null);

        fetch('https://usscfedora.org/training.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
                action: 'create_challenge',
                ...newChallenge
            }),
        })
        .then(response => response.json())
        .then(data => {
            if (data.status === 'success') {
                fetchChallenges();
                setShowModal(false);
                setNewChallenge({
                    title: '',
                    category: 'OSINT',
                    description: '',
                    file_link: '',
                    flag: '',
                    difficulty: 'Medium'
                });
            } else {
                setCreateError(data.message);
            }
        })
        .catch(error => {
            console.error('Error creating challenge:', error);
            setCreateError('Failed to create challenge. Please try again.');
        });
    };

    const handleDeleteChallenge = (challengeId) => {
        if (!window.confirm('Are you sure you want to delete this challenge?')) {
            return;
        }
    
        fetch(`https://usscfedora.org/training.php?id=${challengeId}`, {
            method: 'DELETE',
            credentials: 'include',
        })
        .then(response => response.json())
        .then(data => {
            if (data.status === 'success') {
                fetchChallenges();
                setSelectedChallenge(null);
            } else {
                alert(data.message);
            }
        })
        .catch(error => {
            console.error('Error deleting challenge:', error);
            alert('Failed to delete challenge. Please try again.');
        });
    };

    const handleSubmitFlag = (challengeId, flag) => {
        if (!flag) {
            setSubmissionStatus('Please enter a flag');
            return;
        }

        fetch('https://usscfedora.org/training.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
                action: 'submit_flag',
                challenge_id: challengeId,
                submitted_flag: flag
            }),
        })
        .then(response => response.json())
        .then(data => {
            setSubmissionStatus(data.message);
            if (data.status === 'success') {
                fetchChallenges();
                setFlagInput('');
            }
        })
        .catch(error => {
            console.error('Error submitting flag:', error);
            setSubmissionStatus('Error submitting flag. Please try again.');
        });
    };

    const renderMarkdown = (text) => (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                a: ({node, ...props}) => (
                    <a 
                        {...props} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-blue-500 hover:text-blue-700 underline"
                    />
                ),
                pre: ({node, ...props}) => (
                    <div className="bg-gray-100 p-4 rounded-lg my-4 overflow-x-auto">
                        <pre {...props} />
                    </div>
                ),
                code: ({node, inline, ...props}) => (
                    inline ? 
                        <code className="bg-gray-100 px-1 rounded" {...props} /> :
                        <code {...props} />
                )
            }}
        >
            {text}
        </ReactMarkdown>
    );

    return (
        <Container fluid className="training-page-wrapper">
            <div className="training-main-wrapper">
                <div className="training-header-wrapper">
                    <h2>Training Exercises</h2>
                    <Button 
                        variant="primary" 
                        onClick={() => setShowModal(true)}
                        className="training-create-button"
                    >
                        Create Challenge
                    </Button>
                </div>

                <div className="training-filters-wrapper mb-4">
                    <Row className="g-3">
                        <Col md={4}>
                            <Form.Control
                                type="text"
                                placeholder="Search challenges..."
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                className="training-search-input"
                            />
                        </Col>
                        <Col md={4}>
                            <Form.Select
                                value={filters.category}
                                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                                className="training-category-filter"
                            >
                                <option value="All">All Categories</option>
                                <option value="OSINT">OSINT</option>
                                <option value="Pwn">Pwn</option>
                                <option value="Web">Web</option>
                                <option value="Reverse Engineering">Reverse Engineering</option>
                                <option value="Crypto">Crypto</option>
                                <option value="Forensics">Forensics</option>
                                <option value="Misc">Misc</option>
                                <option value="Steganography">Steganography</option>
                                <option value="Binary Exploitation">Binary Exploitation</option>
                                <option value="Networking">Networking</option>
                                <option value="Programming">Programming</option>
                            </Form.Select>
                        </Col>
                        <Col md={4}>
                            <Form.Select
                                value={filters.difficulty}
                                onChange={(e) => setFilters({ ...filters, difficulty: e.target.value })}
                                className="training-difficulty-filter"
                            >
                                <option value="All">All Difficulties</option>
                                <option value="Easy">Easy</option>
                                <option value="Medium">Medium</option>
                                <option value="Hard">Hard</option>
                                <option value="Extreme">Extreme</option>
                            </Form.Select>
                        </Col>
                    </Row>
                </div>

                <div className="training-grid-wrapper">
                    <Row>
                        {getFilteredChallenges().map(challenge => (
                            <Col key={challenge.id} lg={4} md={6} sm={12} className="training-grid-col">
                                <Card 
                                    onClick={() => setSelectedChallenge(challenge)}
                                    className="training-challenge-card"
                                >
                                    <Card.Body className="training-card-content">
                                        <Card.Title className="training-card-heading">{challenge.title}</Card.Title>
                                        <div className="training-category-tag">{challenge.category}</div>
                                        <div className={`training-difficulty-tag training-difficulty-${challenge.difficulty}`}>
                                            {challenge.difficulty}
                                        </div>
                                        <div className="training-card-desc" style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            maxHeight: '4.5em',
                                            lineHeight: '1.5em'
                                        }}>
                                            {renderMarkdown(challenge.description)}
                                        </div>
                                        {challenge.file_link && (
                                            <Button 
                                                variant="link" 
                                                href={challenge.file_link} 
                                                target="_blank"
                                                className="training-file-link"
                                            >
                                                Download File
                                            </Button>
                                        )}
                                        <Button 
                                            variant="danger" 
                                            size="sm"
                                            className="training-delete-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteChallenge(challenge.id);
                                            }}
                                        >
                                            ×
                                        </Button>
                                    </Card.Body>
                                    <Card.Footer className="training-card-footer">
                                        <small>Created by: {challenge.created_by}</small>
                                    </Card.Footer>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </div>

                {selectedChallenge && (
                    <Modal 
                        show={!!selectedChallenge} 
                        onHide={() => {
                            setSelectedChallenge(null);
                            setSubmissionStatus(null);
                            setFlagInput('');
                        }} 
                        size="lg"
                        className="training-modal"
                    >
                        <Modal.Header closeButton className="training-modal-header">
                            <Modal.Title className="training-modal-title">
                                {selectedChallenge.title}
                                <div className="training-modal-category">{selectedChallenge.category}</div>
                                <div className={`training-difficulty-tag training-difficulty-${selectedChallenge.difficulty}`}>
                                    {selectedChallenge.difficulty}
                                </div>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="training-modal-body">
                            <div className="training-modal-content">
                                <section className="training-desc-section">
                                    <h5>Description</h5>
                                    <div className="prose max-w-none">
                                        {renderMarkdown(selectedChallenge.description)}
                                    </div>
                                    {selectedChallenge.file_link && (
                                        <Button 
                                            variant="outline-primary" 
                                            href={selectedChallenge.file_link} 
                                            target="_blank"
                                            className="training-file-button mt-4"
                                        >
                                            Download File
                                        </Button>
                                    )}
                                </section>

                                <section className="training-flag-section">
                                    <h5>Submit Flag</h5>
                                    <Form className="training-flag-form">
                                        <Form.Group>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter flag"
                                                value={flagInput}
                                                onChange={(e) => setFlagInput(e.target.value)}
                                                className="training-flag-input"
                                            />
                                        </Form.Group>
                                        <Button
                                            variant="primary"
                                            className="training-submit-button"
                                            onClick={() => handleSubmitFlag(selectedChallenge.id, flagInput)}
                                        >
                                            Submit
                                        </Button>
                                        {submissionStatus && (
                                            <div className={`training-status ${
                                                submissionStatus.includes('successfully') ? 'training-success' : 'training-error'
                                            }`}>
                                                {submissionStatus}
                                            </div>
                                        )}
                                    </Form>
                                </section>

                                <section className="training-completion-section">
                                    <h5>Completed By</h5>
                                    {selectedChallenge.completed_by?.length > 0 ? (
                                        <ListGroup className="training-completion-list">
                                            {selectedChallenge.completed_by.map((completion, index) => (
                                                <ListGroup.Item key={index} className="training-completion-item">
                                                    <div className="d-flex justify-content-between align-items-center w-100">
                                                        <span>{completion.username}</span>
                                                        <small className="text-muted">
                                                            {new Date(completion.completed_at).toLocaleString()}
                                                        </small>
                                                    </div>
                                                </ListGroup.Item>
                                            ))}
                                        </ListGroup>
                                    ) : (
                                        <p className="training-no-completions">
                                            No one has completed this challenge yet!
                                        </p>
                                    )}
                                </section>
                            </div>
                        </Modal.Body>
                    </Modal>
                )}

                <Modal 
                    show={showModal} 
                    onHide={() => {
                        setShowModal(false);
                        setCreateError(null);
                        setNewChallenge({
                            title: '',
                            category: 'OSINT',
                            description: '',
                            file_link: '',
                            flag: '',
                            difficulty: 'Medium'
                        });
                    }}
                    className="training-create-modal"
                >
                    <Modal.Header closeButton className="training-create-header">
                        <Modal.Title className="training-create-title">Create Challenge</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="training-create-body">
                        <Form className="training-create-form">
                            {createError && (
                                <div className="training-error-message">{createError}</div>
                            )}
                            <Form.Group className="training-form-group">
                                <Form.Label>Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={newChallenge.title}
                                    onChange={(e) => setNewChallenge({ ...newChallenge, title: e.target.value })}
                                    className="training-input"
                                />
                            </Form.Group>
                            <Form.Group className="training-form-group">
                                <Form.Label>Category</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={newChallenge.category}
                                    onChange={(e) => setNewChallenge({ ...newChallenge, category: e.target.value })}
                                    className="training-select"
                                >
                                    <option>OSINT</option>
                                    <option>Pwn</option>
                                    <option>Web</option>
                                    <option>Reverse Engineering</option>
                                    <option>Crypto</option>
                                    <option>Forensics</option>
                                    <option>Misc</option>
                                    <option>Steganography</option>
                                    <option>Binary Exploitation</option>
                                    <option>Networking</option>
                                    <option>Programming</option>
                                </Form.Control>
                            </Form.Group>
                            <Form.Group className="training-form-group">
                                <Form.Label>Difficulty</Form.Label>
                                <Form.Control
                                    as="select"
                                    value={newChallenge.difficulty}
                                    onChange={(e) => setNewChallenge({ ...newChallenge, difficulty: e.target.value })}
                                    className="training-select"
                                >
                                    <option>Easy</option>
                                    <option>Medium</option>
                                    <option>Hard</option>
                                    <option>Extreme</option>
                                </Form.Control>
                            </Form.Group>
                            <Form.Group className="training-form-group">
                                <Form.Label>Description (Supports Markdown formatting)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    value={newChallenge.description}
                                    onChange={(e) => setNewChallenge({ ...newChallenge, description: e.target.value })}
                                    className="training-textarea font-mono"
                                    placeholder="# Your challenge description here
                                    * Supports **bold** and *italic* text
                                    * Creates clickable [links](https://example.com)
                                    * Allows `code` formatting
                                    * And more!"
                                />
                            </Form.Group>
                            <Form.Group className="training-form-group">
                                <Form.Label>File Link (Optional)</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={newChallenge.file_link}
                                    onChange={(e) => setNewChallenge({ ...newChallenge, file_link: e.target.value })}
                                    className="training-input"
                                />
                            </Form.Group>
                            <Form.Group className="training-form-group">
                                <Form.Label>Flag</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={newChallenge.flag}
                                    onChange={(e) => setNewChallenge({ ...newChallenge, flag: e.target.value })}
                                    className="training-input"
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="training-create-footer">
                        <Button 
                            variant="secondary" 
                            onClick={() => setShowModal(false)} 
                            className="training-cancel-button"
                        >
                            Cancel
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={handleCreateChallenge} 
                            className="training-create-submit"
                        >
                            Create
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </Container>
    );
}

export default Training;